import {
  Avatar,
  Checkbox,
  Form,
  Image,
  Input,
  Modal,
  Popconfirm,
  Select,
  Space,
  Switch,
  Table,
  TableProps,
  Tooltip,
  TreeSelect,
} from 'antd';
import queryString from 'query-string';
import { useEffect, useMemo, useState } from 'react';
import '../../../sass/pages/_dashboard.scss';
import * as Icon from 'react-feather';
import {
  AppstoreAddOutlined,
  CheckSquareOutlined,
  EyeOutlined,
  PictureOutlined,
  StopOutlined,
  ShoppingOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { PermissionEnum } from '../../../enums/Permission.enum';
import { BrandModel, ProductModel, SellerModel } from '../../../interfaces';
import { ListingProductsProps } from './Product.types';
import { TranslatedTag } from './TranslatedTag';
import { SortOrder } from 'antd/lib/table/interface';
import { Check, RefreshCw, Star } from 'react-feather';
import { ProductShopStatusEnum, ProductStatusEnum } from '../../../enums/ProductStatus.enum';
import { useAuthStore } from '../../../store';
import { checkStatusAllProduct } from '../../../utils/checkProductStatus';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';
import { SellerIDsEnum } from '../../../enums/SellerIDs.enum';
import { Images } from '../../../assets/images/Index';

export enum RemovalReasonEnum {
  NOT_OFFERED_ANYMORE = 'not_offered_anymore',
  ERRORS_IN_PRODUCT_INFORMATION = 'errors_in_product_information',
  LICENSING_IMPORTING_ISSUES = 'licensing_importing_issues',
  PRICING_ISSUES = 'pricing_issues',
  BRAND_ISSUES = 'brand_issues',
  SUSPENDED_SELLER = 'suspended_seller',
  REMOVED_SELLER = 'removed_seller',
  OTHER_ISSUES = 'other_issues',
  NO_STOCK = 'no_stock',
}

export const ListingProducts = ({
  loading,
  languages,
  shop,
  products,
  searchValue,
  selectedRows,
  productCount = 0,
  setSelectedRows,
  deleteProducts,
  archiveProducts,
  sendProductsToShop,
  changeProductStatus,
  handleSelectAllProducts,
  shopId,
}: ListingProductsProps) => {
  // hooks
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { search } = useLocation();
  const location = useLocation();
  const [selectedValues, setSelectedValues] = useState<string[]>([]);
  const getParams = new URLSearchParams(location.search);
  const showTranslatedParam = getParams.get('language');
  const [removalModalVisible, setRemovalModalVisible] = useState(false);
  const [productToRemove, setProductToRemove] = useState<string | null>(null);
  const [removalForm] = Form.useForm();

  const checkAccess = (permission: string) => user?.permissions?.includes(permission);
  const isReadonly = !!checkAccess(PermissionEnum.READ_PRODUCT_RESELLER);

  const hasLicenseRequired = (product: any): boolean => {
    try {
      if (shopId === null || shopId === undefined) return false;

      const customsInfoRaw = product?.customs_information;
      const customsInfo =
        typeof customsInfoRaw === 'string'
          ? JSON.parse(customsInfoRaw || '{}')
          : customsInfoRaw ?? {};

      const license = customsInfo?.[String(shopId)]?.license_required;
      if (!license || license === null || license === undefined) return false;
      if (typeof license === 'boolean') return license;
      if (typeof license === 'string') {
        const trimmed = license.trim();
        if (trimmed === '') return false;
        if (trimmed.toLowerCase() === 'no') return false;
        return true;
      }
      return Boolean(license);
    } catch {
      return false;
    }
  };

  const [exportType, setExportType] = useState<
    Record<'main' | 'configurator' | 'image' | 'updateOtherShops', boolean>
  >({
    main: true,
    image: false,
    configurator: false,
    updateOtherShops: false,
  });

  // local state
  const searchParsed = useMemo(() => queryString.parse(search), [search]);

  const handleCopy = (event: any) => {
    const clipboardData = event.clipboardData || window.Clipboard;
    const selectedText = window?.getSelection()?.toString().trim();

    clipboardData.setData('text/plain', selectedText);
    event.preventDefault();
  };

  const handleDeleteById = (id: string) => () => deleteProducts([id]);
  const handleArchiveById = (id: string) => () => archiveProducts([id]);

  const handleSendInShop = (id: string, type?: { main: boolean; image: boolean }) => async () => {
    if (sendProductsToShop && id) {
      sendProductsToShop([id], type || exportType);

      setExportType({
        main: true,
        image: false,
        configurator: false,
        updateOtherShops: false,
      });
    }
  };

  const language = languages.find(i => i.iso_code === searchValue?.language);

  const orderParams = useMemo(
    () =>
      searchParsed?.orderBy && searchParsed?.orderKey
        ? {
            orderKey: searchParsed.orderKey.toString(),
            orderBy: (searchParsed?.orderBy == 'asc'
              ? 'ascend'
              : 'descend') as unknown as SortOrder,
          }
        : null,
    [searchParsed],
  );

  const handleViewProduct = (id: string) => () => {
    navigate({ search: queryString.stringify({ ...searchParsed, id }) }, { replace: true });
  };

  const handleChange: TableProps<ProductModel>['onChange'] = (
    _pagination,
    _filters,
    sorter: any,
  ) => {
    navigate(
      {
        search: queryString.stringify(
          {
            ...searchParsed,
            ...(sorter?.order
              ? {
                  orderKey: sorter?.columnKey,
                  orderBy: sorter?.order == 'ascend' ? 'asc' : 'desc',
                }
              : { orderKey: null, orderBy: null }),
          },
          { skipNull: true },
        ),
      },
      { replace: true },
    );
  };

  const onChange = (value: string[]) => {
    const filteredParsed = Object.entries(searchParsed).filter(
      ([key, _value]) => !key.includes('show'),
    );
    const parsed = Object.fromEntries([...value.map(i => i.split('-')), ...filteredParsed]);

    navigate({ search: queryString.stringify({ ...parsed }) }, { replace: true });
  };

  useEffect(() => {
    const { search } = location;
    const searchParsed = queryString.parse(search);

    const selectedValues = Object.entries(searchParsed)
      .filter(([key, _value]) => key.includes('show'))
      .map(([key, _value]) => key + '-' + _value);

    setSelectedValues(selectedValues);
  }, [location.search]);

  const treeData = [
    {
      value: 'show_translated',
      title: 'Translated',
      disabled: true,
      children: [
        {
          value: 'show_translated-1',
          title: 'Yes',
        },
        {
          value: 'show_translated-0',
          title: 'No',
        },
      ],
    },
    {
      value: 'show_categories',
      title: 'Categories',
      disabled: true,
      children: [
        {
          value: 'show_categories-1',
          title: 'Yes',
        },
        {
          value: 'show_categories-0',
          title: 'No',
        },
      ],
    },
    {
      value: 'brands',
      title: 'Brands',
      disabled: true,
      children: [
        {
          value: 'show_brands-1',
          title: 'Yes',
        },
        {
          value: 'show_brands-0',
          title: 'No',
        },
      ],
    },
    {
      value: 'updated',
      title: 'Has changed',
      disabled: true,
      children: [
        {
          value: 'show_updated-2',
          title: 'Yes',
        },
        {
          value: 'show_updated-0',
          title: 'No',
        },
      ],
    },
    {
      value: 'show_ean',
      title: 'Has EAN',
      disabled: true,
      children: [
        {
          value: 'show_ean-1',
          title: 'Yes',
        },
        {
          value: 'show_ean-0',
          title: 'No',
        },
      ],
    },
    {
      value: 'show_images',
      title: 'Has images',
      disabled: true,
      children: [
        {
          value: 'show_images-1',
          title: 'Yes',
        },
        {
          value: 'show_images-0',
          title: 'No',
        },
      ],
    },
    {
      value: 'show_price',
      title: 'Has Price',
      disabled: true,
      children: [
        {
          value: 'show_price-1',
          title: 'Yes',
        },
        {
          value: 'show_price-0',
          title: 'No',
        },
      ],
    },
    {
      value: 'show_stock',
      title: 'Has Stock',
      disabled: true,
      children: [
        {
          value: 'show_stock-1',
          title: 'Yes',
        },
        {
          value: 'show_stock-0',
          title: 'No',
        },
      ],
    },
    {
      value: 'show_cncode',
      title: 'Has CN Code',
      disabled: true,
      children: [
        {
          value: 'show_cncode-1',
          title: 'Yes',
        },
        {
          value: 'show_cncode-0',
          title: 'No',
        },
      ],
    },
    {
      value: 'show_is_in_vendor_list',
      title: 'In vendor list',
      disabled: true,
      children: [
        {
          value: 'show_is_in_vendor_list-1',
          title: 'Yes',
        },
        {
          value: 'show_is_in_vendor_list-0',
          title: 'No',
        },
      ],
    },
    {
      value: 'show_has_activity',
      title: 'Has activity',
      disabled: true,
      children: [
        {
          value: 'show_has_activity-1',
          title: 'Yes',
        },
        {
          value: 'show_has_activity-0',
          title: 'No',
        },
      ],
    },
    {
      value: 'show_is_express_delivery',
      title: 'Is express delivery',
      disabled: true,
      children: [
        {
          value: 'show_is_express_delivery-1',
          title: 'Yes',
        },
        {
          value: 'show_is_express_delivery-0',
          title: 'No',
        },
      ],
    },
    ...(searchParsed?.status === ProductShopStatusEnum.IN_SHOP
      ? [
          {
            value: 'show_sales_channel',
            title: 'Sales channel',
            disabled: true,
            children: [
              {
                value: 'show_sales_channel-b2c',
                title: 'B2C',
              },
              {
                value: 'show_sales_channel-b2b',
                title: 'B2B',
              },
            ],
          },
        ]
      : []),
  ];

  const onClear = () => {
    navigate(
      {
        search: queryString.stringify({
          status: searchParsed?.status,
          seller: searchParsed?.seller,
          category: searchParsed?.category,
          user: searchParsed?.user,
          removal_reason: searchParsed?.removal_reason,
        }),
      },
      { replace: true },
    );
  };

  const handleShowRemovalModal = (id: string) => {
    setProductToRemove(id);
    setRemovalModalVisible(true);
  };

  const handleRemovalCancel = () => {
    setRemovalModalVisible(false);
    setProductToRemove(null);
    removalForm.resetFields();
  };

  const handleRemovalConfirm = async () => {
    try {
      const values = await removalForm.validateFields();
      if (values.reason === RemovalReasonEnum.OTHER_ISSUES && !values.additional_infos) {
        removalForm.setFields([
          {
            name: 'additional_infos',
            errors: ['Additional information is required when selecting Other'],
          },
        ]);
        return;
      }

      if (productToRemove && deleteProducts) {
        await deleteProducts([productToRemove], {
          removal_reason: values.reason,
          additional_infos: values.additional_infos,
        });
        handleRemovalCancel();
      }
    } catch (error) {
      // Form validation error
    }
  };

  const columns = [
    {
      title: 'Thumbnail',
      render: (e: any) => {
        if (e?.product_images?.length == 0) {
          return (
            <div className="images-wrapper">
              <div className="extra-images full-height">
                <Avatar
                  shape="square"
                  size={{ sm: 50, xxl: 50 }}
                  style={{
                    width: '50px',
                    height: '50px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                  alt="Default product image"
                  icon={<PictureOutlined />}
                />
              </div>
            </div>
          );
        } else {
          const findImage = e?.product_images?.find(
            (x: any) => x.is_cover === 1 && x.is_deleted === 0,
          );

          return (
            <Image
              width={50}
              height={50}
              src={findImage?.url == undefined ? e?.product_images?.[0]?.url : findImage?.url}
              onCopy={handleCopy}
              alt=""
              style={{ objectFit: 'contain' }}
            />
          );
        }
      },
    },
    {
      title: 'SKU Intern',
      dataIndex: 'sku_intern',
      key: 'sku_intern',
      sorter: true,
      minWidth: '120px',
      sortOrder: orderParams?.orderKey === 'sku_intern' ? orderParams.orderBy : null,
      ...(orderParams?.orderKey === 'sku_intern' && { defaultSortOrder: orderParams.orderBy }),
      render: (value: string, data: any) => (
        <span
          className="table__cursor"
          onClick={handleViewProduct(data.id)}
          onCopy={handleCopy}
          style={{ textDecoration: data.is_in_vendor_list ? 'none' : 'line-through' }}
        >
          {hasLicenseRequired(data) && (
            <Tooltip title="License required">
              <StopOutlined style={{ color: '#cf1322', marginRight: 6, fontSize: 16 }} />
            </Tooltip>
          )}
          {value}
          {!data.is_simple && (
            <img
              src={Images.variants}
              alt="Variants"
              style={{ marginLeft: '5px', width: '16px', height: '16px' }}
            />
          )}
        </span>
      ),
    },
    {
      title: 'SKU Ext.',
      dataIndex: 'sku',
      minWidth: '120px',
      key: 'sku',
      sorter: true,
      sortOrder: orderParams?.orderKey === 'sku' ? orderParams.orderBy : null,
      ...(orderParams?.orderKey === 'sku' && { defaultSortOrder: orderParams.orderBy }),
      render: (value: string, data: any) => (
        <span
          className="table__cursor"
          onClick={handleViewProduct(data.id)}
          onCopy={handleCopy}
          style={{ textDecoration: data.is_in_vendor_list ? 'none' : 'line-through' }}
        >
          {value}
        </span>
      ),
    },
    {
      title: `Reseller`,
      dataIndex: 'seller',
      key: 'seller',
      render: (value: SellerModel, data: any) => (
        <span style={{ textDecoration: data.is_in_vendor_list ? 'none' : 'line-through' }}>
          {value?.name}
        </span>
      ),
    },
    {
      title: `Brand`,
      dataIndex: 'brand',
      key: 'brand',
      render: (value: BrandModel, data: any) => (
        <span style={{ textDecoration: data.is_in_vendor_list ? 'none' : 'line-through' }}>
          {value?.name}
        </span>
      ),
    },
    {
      title: 'Product name',
      dataIndex: 'product_translates',
      key: 'name',
      sorter: true,
      width: '330px',
      sortOrder: orderParams?.orderKey === 'name' ? orderParams.orderBy : null,
      ...(orderParams?.orderKey === 'name' && { defaultSortOrder: orderParams.orderBy }),
      render: (values: any[], data: any) => (
        <p
          className="text__dots table__cursor"
          onClick={handleViewProduct(data.id)}
          onCopy={handleCopy}
          style={{ textDecoration: data.is_in_vendor_list ? 'none' : 'line-through' }}
        >
          {showTranslatedParam == null
            ? values.find(i => i.language_id === language?.id)?.name
            : values.find(i => i.language_id === showTranslatedParam)?.name}
        </p>
      ),
    },
    {
      title: 'Stock',
      dataIndex: 'stock',
      key: 'stock',
      sorter: true,
      sortOrder: orderParams?.orderKey === 'stock' ? orderParams.orderBy : null,
      ...(orderParams?.orderKey === 'stock' && { defaultSortOrder: orderParams.orderBy }),
      render: (stock: number, data: ProductModel) => (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            textDecoration: data.is_in_vendor_list ? 'none' : 'line-through',
          }}
        >
          {searchParsed?.status === ProductShopStatusEnum.IN_SHOP ? (
            data?.productShop?.stock_updated ? (
              <Tooltip
                placement="rightTop"
                title={'Stock not yet updated in Shopware'}
                className="more-info"
              >
                <div
                  style={{
                    width: '9px',
                    height: '9px',
                    borderRadius: '50%',
                    backgroundColor: 'red',
                    marginRight: '8px',
                  }}
                />
              </Tooltip>
            ) : (
              <Tooltip
                placement="rightTop"
                title={'Stock updated in Shopware'}
                className="more-info"
              >
                <div
                  style={{
                    width: '9px',
                    height: '9px',
                    borderRadius: '50%',
                    backgroundColor: 'green',
                    marginRight: '8px',
                  }}
                />
              </Tooltip>
            )
          ) : null}
          {stock}
        </div>
      ),
    },
    ...([ProductShopStatusEnum.IN_SHOP, ProductShopStatusEnum.REMOVED].includes(
      searchParsed?.status as any,
    ) && checkAccess(PermissionEnum.UPDATE_PRODUCT_DATA)
      ? [
          {
            title: 'Visibility',
            dataIndex: 'productShop',
            key: 'productShop',
            width: 0,
            render: (value: ProductModel['productShop']) => (
              <Switch
                checked={!!value?.status}
                className={
                  searchParsed?.status === ProductShopStatusEnum.IN_SHOP ||
                  searchParsed?.status === ProductShopStatusEnum.REMOVED
                    ? 'prevent__clicking'
                    : ''
                }
              />
            ),
          },
        ]
      : []),
    ...(!searchParsed?.status
      ? [
          {
            title: 'Status',
            key: 'status',
            dataIndex: 'status',
            width: 0,
            render: (value: any, data: any) => {
              const statusText = checkStatusAllProduct(value, data?.productShop?.status);
              return (
                <div style={{ textAlign: 'center' }}>
                  <div className={statusText}>{statusText?.replace('-', ' ')}</div>
                </div>
              );
            },
          },
        ]
      : []),
    {
      title: (
        <div
          style={{
            width: '100%',
            display: 'flex',
            justifyContent: 'space-between',
          }}
        >
          <TreeSelect
            multiple
            className="tree__select"
            style={{ width: '100%' }}
            dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
            placeholder="Filter"
            allowClear
            treeDefaultExpandAll
            onChange={onChange}
            treeData={treeData}
            onClear={onClear}
            treeCheckable={false}
            treeCheckStrictly={false}
            popupClassName="tree__select-wrapper"
            value={selectedValues}
          />
        </div>
      ),
      key: 'action',
      width: 360,
      render: (e: any) => {
        const isInShop =
          e.status === ProductStatusEnum.IN_SHOP ||
          e.productShop?.status === ProductShopStatusEnum.IN_SHOP;
        const isUpdated =
          e.productShop?.status === ProductShopStatusEnum.IN_SHOP
            ? e?.productShop?.is_updated
            : e.is_updated;

        const labelUpdate = isInShop
          ? `Product in shop ${isUpdated ? 'is updated' : ''}`
          : 'Product not in shop';

        return (
          <Space>
            <Tooltip title="Is product translated" placement="top">
              <p>
                <TranslatedTag
                  languages={languages}
                  shop={shop}
                  translates={e.product_translates}
                />
              </p>
            </Tooltip>
            <Tooltip title="Product categories" placement="top">
              <span>
                <AppstoreAddOutlined
                  className={
                    e?.product_categories?.some(
                      (item: any) => item.seller_id == SellerIDsEnum.SHOPWARE,
                    )
                      ? 'success-category'
                      : 'not-success'
                  }
                  size={17}
                />
              </span>
            </Tooltip>
            <Tooltip title="Product Brands" placement="top">
              <a className={e?.brand?.mapped_brand ? 'success-category' : 'not-success-approve'}>
                <Star size={15} />
              </a>
            </Tooltip>
            <Tooltip title="Variant Price" placement="top">
              <p
                className={
                  e.price_extended == null ||
                  e.price_extended[Number(shopId)] == undefined ||
                  e.price_extended[Number(shopId)].price == undefined
                    ? 'not-success-approve'
                    : e.price_extended[Number(shopId)].price == null ||
                      e.price_extended[Number(shopId)].price == 0 ||
                      e.price_extended[Number(shopId)].price == undefined
                    ? 'not-success-approve'
                    : 'success-category'
                }
              >
                {shop?.currencies && JSON.parse(String(shop?.currencies)).map((c: any) => c.symbol)}
              </p>
            </Tooltip>
            {e?.status != ProductStatusEnum.APPROVED && (
              <Tooltip title="Is product approved" placement="top">
                <a
                  className={
                    e?.status == ProductStatusEnum.APPROVED ||
                    e?.status == ProductStatusEnum.IN_SHOP ||
                    e?.productShop?.status == ProductShopStatusEnum.IN_SHOP
                      ? 'success-category'
                      : 'not-success-approve'
                  }
                >
                  <Check size={16} />
                </a>
              </Tooltip>
            )}

            <Tooltip title={labelUpdate} placement="top">
              <a
                className={
                  e?.productShop?.status == ProductShopStatusEnum.IN_SHOP && e.is_updated == true
                    ? 'success-category-red'
                    : e?.productShop?.status != ProductShopStatusEnum.IN_SHOP
                    ? 'not-success-approve'
                    : 'success-category'
                }
              >
                <RefreshCw size={16} />
              </a>
            </Tooltip>

            {(e?.productShop?.status === ProductShopStatusEnum.REMOVED ||
              e?.productShop?.status === ProductShopStatusEnum.ARCHIVED) &&
              e?.productShop?.removal_reason && (
                <Tooltip
                  title={`Removal reason: ${e.productShop.removal_reason
                    .replace(/_/g, ' ')
                    .toLowerCase()}`}
                  placement="top"
                >
                  <a className="action-item" style={{ color: '#ff9800' }}>
                    <Icon.AlertTriangle size={16} />
                  </a>
                </Tooltip>
              )}

            {Number(searchParsed?.status) === ProductStatusEnum.PENDING && !isReadonly && (
              <Popconfirm
                title={
                  <div>
                    <p className="title">{t('send_product_in_approve')}</p>
                    <p className="text">{t('send_product_in_shop_subtitle')}</p>
                  </div>
                }
                okText="Yes"
                cancelText="No"
                onConfirm={() => changeProductStatus(e.id)}
              >
                <Tooltip title="Approve this product" placement="top">
                  <a className="action-item">
                    <CheckSquareOutlined size={20} />
                  </a>
                </Tooltip>
              </Popconfirm>
            )}

            {[ProductStatusEnum.APPROVED].includes(Number(searchParsed?.status)) && !isReadonly && (
              <Popconfirm
                title={
                  <div>
                    <p className="title">{t('send_product_in_shop')}</p>
                  </div>
                }
                okText="Yes"
                cancelText="No"
                onConfirm={handleSendInShop(e.id || '', { main: true, image: true })}
              >
                <Tooltip title="Send product in shop" placement="top">
                  <a className="action-item">
                    <ShoppingOutlined size={20} alt="Send to shop" />
                  </a>
                </Tooltip>
              </Popconfirm>
            )}

            {[ProductShopStatusEnum.IN_SHOP, ProductShopStatusEnum.REMOVED].includes(
              (searchParsed?.status || '') as any,
            ) &&
              !isReadonly && (
                <Popconfirm
                  title={
                    <div>
                      <p className="title">{t('send_product_in_shop')}</p>
                      <div
                        style={{
                          display: 'flex',
                          marginBottom: '40px',
                          alignItems: 'start',
                          flexFlow: 'column',
                        }}
                      >
                        <div>
                          <Checkbox
                            defaultChecked
                            disabled
                            onChange={(e: CheckboxChangeEvent) => {
                              setExportType({ ...exportType, main: e?.target?.checked });
                            }}
                          >
                            Main data
                          </Checkbox>
                        </div>
                        <div>
                          <Checkbox
                            checked={exportType.image}
                            onChange={(e: CheckboxChangeEvent) => {
                              setExportType({ ...exportType, image: e?.target?.checked });
                            }}
                          >
                            Repair Images
                          </Checkbox>
                        </div>
                        <div>
                          <Checkbox
                            checked={exportType.configurator}
                            onChange={(e: CheckboxChangeEvent) => {
                              setExportType({ ...exportType, configurator: e?.target?.checked });
                            }}
                          >
                            Repair Product configurators
                          </Checkbox>
                        </div>
                        <div>
                          <Checkbox
                            checked={exportType.updateOtherShops}
                            onChange={(e: CheckboxChangeEvent) => {
                              setExportType({
                                ...exportType,
                                updateOtherShops: e?.target?.checked,
                              });
                            }}
                          >
                            Update in other shops
                          </Checkbox>
                        </div>
                      </div>
                    </div>
                  }
                  okText="Yes"
                  cancelText="No"
                  onConfirm={handleSendInShop(e.id || '')}
                >
                  <Tooltip title="Send product in shop" placement="top">
                    <a className="action-item">
                      <ShoppingOutlined size={20} alt="Send to shop" />
                    </a>
                  </Tooltip>
                </Popconfirm>
              )}

            {(ProductStatusEnum.IGNORED === Number(searchParsed?.status) ||
              ProductShopStatusEnum.REMOVED ===
                ((searchParsed?.status || '')?.toString() as any)) &&
              checkAccess(PermissionEnum.DELETE_PRODUCT) &&
              !isReadonly && (
                <Popconfirm
                  title={
                    <div>
                      <p className="title">{t('archive_product')}</p>
                      <p className="text">{t('archive_subtitle_product')}</p>
                    </div>
                  }
                  okText="Yes"
                  cancelText="No"
                  onConfirm={handleArchiveById(e.id)}
                >
                  <Tooltip title="Archive" placement="top">
                    <a className="action-item">
                      <Icon.Archive size={13} />
                    </a>
                  </Tooltip>
                </Popconfirm>
              )}
            <Tooltip title="View product" placement="top">
              <a className="action-item" onClick={handleViewProduct(e.id)}>
                <EyeOutlined size={17} alt="View" />
              </a>
            </Tooltip>
            {ProductShopStatusEnum.IN_SHOP === (searchParsed?.status || '') &&
              checkAccess(PermissionEnum.DELETE_PRODUCT) &&
              !isReadonly && (
                <Tooltip title="Remove (hide from shop)" placement="right">
                  <a className="action-item" onClick={() => handleShowRemovalModal(e.id)}>
                    <Icon.XOctagon size={16} />
                  </a>
                </Tooltip>
              )}

            <Modal
              title={t('delete_remove_from_shop')}
              open={removalModalVisible}
              onCancel={handleRemovalCancel}
              onOk={handleRemovalConfirm}
              okText="Yes"
              cancelText="No"
            >
              <div>
                <p className="text">{t('delete_subtitle_remove_from_shop')}</p>
              </div>
              <Form form={removalForm} layout="vertical" style={{ marginTop: '20px' }}>
                <Form.Item
                  name="reason"
                  label="Removal Reason"
                  rules={[{ required: true, message: 'Please select a removal reason' }]}
                >
                  <Select placeholder="Select a reason">
                    <Select.Option value={RemovalReasonEnum.NOT_OFFERED_ANYMORE}>
                      Not offered anymore
                    </Select.Option>
                    <Select.Option value={RemovalReasonEnum.ERRORS_IN_PRODUCT_INFORMATION}>
                      Errors in product information
                    </Select.Option>
                    <Select.Option value={RemovalReasonEnum.LICENSING_IMPORTING_ISSUES}>
                      Licensing/Importing issues
                    </Select.Option>
                    <Select.Option value={RemovalReasonEnum.PRICING_ISSUES}>
                      Pricing issues
                    </Select.Option>
                    <Select.Option value={RemovalReasonEnum.BRAND_ISSUES}>
                      Brand issues
                    </Select.Option>
                    <Select.Option value={RemovalReasonEnum.SUSPENDED_SELLER}>
                      Suspended seller
                    </Select.Option>
                    <Select.Option value={RemovalReasonEnum.REMOVED_SELLER}>
                      Removed seller
                    </Select.Option>
                    <Select.Option value={RemovalReasonEnum.NO_STOCK}>No stock</Select.Option>
                    <Select.Option value={RemovalReasonEnum.OTHER_ISSUES}>
                      Other issues
                    </Select.Option>
                  </Select>
                </Form.Item>

                <Form.Item
                  noStyle
                  shouldUpdate={(prevValues, currentValues) =>
                    prevValues?.reason !== currentValues?.reason
                  }
                >
                  {({ getFieldValue }) => (
                    <Form.Item
                      name="additional_infos"
                      label="Additional Information"
                      rules={[
                        {
                          required: getFieldValue('reason') === RemovalReasonEnum.OTHER_ISSUES,
                          message: 'Please provide additional information',
                        },
                      ]}
                    >
                      <Input.TextArea
                        rows={4}
                        placeholder="Enter additional details about the removal reason"
                      />
                    </Form.Item>
                  )}
                </Form.Item>
              </Form>
            </Modal>
          </Space>
        );
      },
    },
  ];

  return (
    <div>
      <div
        style={{
          display: 'flex',
          flexFlow: 'row',
          marginBottom: '10px',
        }}
      >
        <span className="select-btn">
          {selectedRows.length > 0 ? `Selected ${selectedRows.length} items` : ''}
        </span>
        {productCount <= 10000 && selectedRows.length > 0 && (
          <>
            {'  '}
            <span className="select-btn-green" onClick={handleSelectAllProducts}>
              Select all {productCount} products
            </span>
          </>
        )}
      </div>

      <Table
        loading={loading}
        className="table"
        columns={columns}
        pagination={false}
        dataSource={products}
        onChange={handleChange}
        rowKey={({ id }) => id}
        rowSelection={{
          selectedRowKeys: selectedRows,
          onChange: (selectedRowKeys, _selectItem, info) => {
            // if (info.type === 'all') {
            //   handleSelectAllProducts?.();
            //   return;
            // }

            // if (info.type === 'single' && selectedRowKeys.length === 0) {
            //   setSelectedRows([]);
            //   return;
            // }

            // if (info.type === 'single' && selectedRows.length > (products?.length || 1)) {
            //   const unSelectedItems =
            //     products?.filter(p => !selectedRowKeys.find(row => row === p.id)) || [];

            //   const filterSelected = selectedRows.filter(
            //     row => !unSelectedItems.find(p => row === p.id),
            //   );

            //   setSelectedRows(filterSelected);
            //   return;
            // }

            setSelectedRows(selectedRowKeys as string[]);
          },
        }}
      />
    </div>
  );
};
